import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";

import type { Point } from "@/landing/system/sections";
import { BASE_URL } from "@/common/links";
import {
  MAC_EXTENSION_ASSET,
  MAC_INTEL_EXTENSION_ASSET,
} from "@/features/wonda/constants";
import {
  useReleaseDownload,
  useResumeReleaseDownload,
} from "@/features/wonda/releaseDownloads";
import { PrimaryLink, TextLink } from "@/landing/system/actions";
import { Ground, Section, SectionHeading } from "@/landing/system/layout";
import { Steps } from "@/landing/system/sections";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

/**
 * Public update page for the Wonda extension in Claude Desktop (MCPB). The
 * extension bundles its own wonda binary and Claude Desktop never updates a
 * file-installed extension, so this is the one-button path the update notice
 * (release-cli.yml MCPB_CMD) and the wonda_update tool point people to.
 * The page is public, but releases are private: clicking Update asks a
 * signed-out visitor to sign in once, then the download starts by itself when
 * they land back here.
 */
export const Route = createFileRoute("/_steward/update")({
  component: UpdatePage,
  head: () => ({
    meta: [
      { title: "Update Wonda for Claude" },
      {
        name: "description",
        content: "Get the latest Wonda extension for Claude in one click.",
      },
    ],
    links: [{ rel: "canonical", href: `${BASE_URL}/update` }],
  }),
});

const OFFERED_ASSETS = [MAC_EXTENSION_ASSET, MAC_INTEL_EXTENSION_ASSET];

const STEPS: Point[] = [
  {
    title: "Download the update",
    body: "Click Update Wonda above. It downloads one file.",
  },
  {
    title: "Open the file",
    body: "It’s in your Downloads folder. Claude opens and asks to install Wonda.",
  },
  {
    title: "Click Install",
    body: (
      <>
        Your connected accounts stay connected. If Claude asks for your Wonda
        API key, copy it from{" "}
        <TextLink to="/account/api-key">your account</TextLink>.
      </>
    ),
  },
];

function UpdatePage() {
  const download = useReleaseDownload();
  useResumeReleaseDownload(download.start, OFFERED_ASSETS);
  const onDownload =
    (asset: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      void download.start(asset);
    };
  return (
    <>
      <Ground lattice className="-mt-(--header-height)">
        <div className="px-(--g-margin) pt-[calc(var(--header-height)+clamp(56px,8vw,112px))] pb-[clamp(56px,7vw,104px)]">
          <img
            src={WabDockIcon}
            alt=""
            width={64}
            height={64}
            className="mb-8 block size-16"
          />
          <SectionHeading
            as="h1"
            eyebrow="Claude Desktop extension"
            title="Update Wonda for Claude"
            sub="Get the latest Wonda for Claude Desktop in three steps. Your connected accounts stay connected."
          />
          <div className="mt-[34px] flex flex-wrap items-center gap-x-6 gap-y-4">
            <PrimaryLink
              href="/update"
              onClick={onDownload(MAC_EXTENSION_ASSET)}
              aria-busy={download.pending}
            >
              <DownloadIcon aria-hidden className="size-4" />
              {download.pending ? "Starting…" : "Update Wonda"}
            </PrimaryLink>
            <p className="m-0 text-[13px] text-(--g-ink-3)">
              Older Mac with an Intel chip?{" "}
              <TextLink
                to="/update"
                onClick={onDownload(MAC_INTEL_EXTENSION_ASSET)}
                className="text-[13px] text-(--g-ink-2)"
              >
                Use this version instead
              </TextLink>
              .
            </p>
          </div>
          {download.error && (
            <p role="alert" className="mt-4 mb-0 text-[14px] text-red-700">
              {download.error}
            </p>
          )}
        </div>
      </Ground>

      <Section>
        <SectionHeading eyebrow="How to update" title="Three steps." />
        <Steps className="mt-14" items={STEPS} />
      </Section>
    </>
  );
}
